import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiFetch } from '../apiClient';
import { ErrorBanner } from './Configuration';
import { TabBar, activeTabKey } from './registerTabs';
import { useApp } from '../appContext';
import { REGISTER_TABS } from './ProjectRegisters';

/**
 * The customer portal's copy of Project Registers. `TABS` is derived from `REGISTER_TABS` so the two
 * pages can't drift on order/labels, and every tab renders the same shared component the internal
 * page uses -- `readOnly` gated on `editableProjectIds` (item 5, 2026-09-12; enforced server-side too)
 * and `portal` always true (distinct from `readOnly`: a few things, like RAAIDD's "Show removed",
 * stay staff-only even for a canEdit customer -- see each register's own comment). No coverage calls
 * or fixed-fee/archived filtering here -- `/…/projects-with-items` isn't customer-reachable, and
 * `GET /projects` already returns only this contact's granted projects.
 */
const TABS = REGISTER_TABS.map(({ key, label, Component }) => ({ key, label, Component }));

export default function CustomerProjectRegisters() {
  // Tab in the URL, same as the internal page: a register is the kind of thing people send each
  // other a link to.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = activeTabKey(searchParams.get('tab'), TABS);

  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { currentUser } = useApp();

  useEffect(() => {
    apiFetch('/projects')
      .then((rows) => {
        setProjects(rows);
        // The common case: a contact is granted exactly one project, so make them pick nothing.
        if (rows.length === 1) setProjectId(rows[0].id);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const active = TABS.find((t) => t.key === tab);
  const ActiveTab = active.Component;
  const canEdit = !!projectId && !!currentUser?.editableProjectIds?.includes(projectId);

  return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Project Registers</div>
        <h1 className="page-title">Project Registers</h1>
        <div className="page-subtitle">
          The shared logs for your project — the project team's contacts, approved change requests,
          the RAAIDD log, the billed-as-used balance, project events, and access requests.
        </div>
      </div>

      <ErrorBanner error={error} />

      {/* Only worth a control when there's a choice to make. */}
      {projects.length > 1 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="form-group" style={{ marginBottom: 0, maxWidth: 340 }}>
            <label className="form-label">Project</label>
            <select
              className="form-select"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
            >
              <option value="">Select a project…</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <TabBar
        tabs={TABS}
        active={tab}
        // `replace`, so clicking through tabs doesn't bury the previous page several entries deep
        // in the browser's Back history.
        onSelect={(key) => setSearchParams({ tab: key }, { replace: true })}
      />

      {!projectId ? (
        <div className="placeholder-page">
          <div className="placeholder-icon">🗂️</div>
          <div className="placeholder-title">{loading ? 'Loading…' : 'No project selected'}</div>
        </div>
      ) : (
        // Keyed by tab AND project, so switching either remounts the tab body rather than showing
        // the last one's filters/edits over the new one's rows.
        <ActiveTab
          key={`${tab}:${projectId}`}
          projectId={projectId}
          portal
          {...(active.key === 'access-requests' ? {} : { readOnly: !canEdit })}
        />
      )}
    </div>
  );
}
